import React, { useCallback, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { alertStore } from '../alert/alertStore';
import api from '../common/api';
import { statesAndProvinces } from '../common/states';
import { formatPhoneNumber } from '../format/PhoneNumber';

type BasisNumber = { ani?: string } | null | undefined;

export type BuyInfoCenterModalProps = {
  show: boolean;
  facility: { id: number };
  basisNumber: BasisNumber;
  onClose(saved: boolean): void;
};

type SearchOptions = {
  areaCode: string;
  zipCode: string;
  city: string;
  state: string;
};

function initialSearch(basisNumber: BasisNumber): SearchOptions {
  const ani = basisNumber?.ani;
  return {
    areaCode: ani != null && ani.length >= 3 ? ani.substring(0, 3) : '',
    zipCode: '',
    city: '',
    state: '',
  };
}

function searchParams(options: SearchOptions): Record<string, string> {
  const params: Record<string, string> = {};
  if (options.areaCode.trim() !== '') {
    params.areaCode = options.areaCode.trim();
  }
  if (options.zipCode.trim() !== '') {
    params.zipCode = options.zipCode.trim();
  }
  if (options.city.trim() !== '') {
    params.city = options.city.trim();
  }
  if (options.state.trim() !== '' && options.state !== '?') {
    params.state = options.state.trim();
  }
  return params;
}

const BuyInfoCenterModal: React.FC<BuyInfoCenterModalProps> = ({
  show,
  facility,
  basisNumber,
  onClose,
}) => {
  const [searchOptions, setSearchOptions] = useState<SearchOptions>(() =>
    initialSearch(basisNumber)
  );
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) {
      return;
    }
    setSearchOptions(initialSearch(basisNumber));
    setSearchResults(null);
    setError(null);
  }, [show, basisNumber]);

  const handleClose = useCallback(
    (saved: boolean) => {
      onClose(saved);
    },
    [onClose]
  );

  const setField = useCallback((patch: Partial<SearchOptions>) => {
    setSearchOptions((o) => ({ ...o, ...patch }));
  }, []);

  const search = useCallback(async () => {
    setSearching(true);
    setError(null);
    try {
      const res = await api.get<string[]>('/smsvoice-number/available', {
        params: searchParams(searchOptions),
      });
      const data = res.data;
      setSearchResults(Array.isArray(data) ? data : []);
    } catch {
      setSearchResults([]);
      setError('Search failed');
    } finally {
      setSearching(false);
    }
  }, [searchOptions]);

  const purchase = useCallback(
    (number: string) => {
      setPurchasing(true);
      setError(null);
      void api
        .post<{ success?: boolean }>('/facility-ani', {
          facilityId: facility.id,
          name: 'Info Center',
          ani: number,
          purchaseNumber: true,
        })
        .then(
          (res) => {
            setPurchasing(false);
            if (!res.data?.success) {
              setError('An unexpected error occurred');
              return;
            }
            alertStore.success('The Info Center number was purchased');
            handleClose(true);
          },
          () => {
            setPurchasing(false);
            setError('Purchase failed');
          }
        );
    },
    [facility.id, handleClose]
  );

  const busy = searching || purchasing;

  return (
    <Modal show={show} onHide={() => !busy && handleClose(false)} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Buy Info Center Number</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error != null && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Row>
          <Col md={12}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Area Code:</Form.Label>
                  <Form.Control
                    value={searchOptions.areaCode}
                    disabled={busy}
                    onChange={(e) => setField({ areaCode: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Zip Code:</Form.Label>
                  <Form.Control
                    value={searchOptions.zipCode}
                    disabled={busy}
                    onChange={(e) => setField({ zipCode: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City:</Form.Label>
                  <Form.Control
                    value={searchOptions.city}
                    disabled={busy}
                    onChange={(e) => setField({ city: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State:</Form.Label>
                  <Form.Select
                    value={searchOptions.state}
                    disabled={busy}
                    onChange={(e) => setField({ state: e.target.value })}>
                    <option value="">-- State --</option>
                    {statesAndProvinces.map((s) => (
                      <option key={s.abbr} value={s.abbr}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div style={{ marginBottom: 15 }}>
              <Button type="button" variant="secondary" disabled={searching || purchasing} onClick={() => void search()}>
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </Col>
          <Col md={12}>
            <table className="table table-striped table-hover table-bordered">
              <thead>
                <tr>
                  <th>Number</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(searchResults ?? []).map((num) => (
                  <tr key={num}>
                    <td>{formatPhoneNumber(num)}</td>
                    <td>
                      <Button
                        type="button"
                        variant="primary"
                        disabled={purchasing}
                        onClick={() => purchase(num)}>
                        {purchasing ? 'Purchasing...' : 'Purchase'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => handleClose(false)}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BuyInfoCenterModal;
